import { type DefaultRootState } from '@databases/redux';
import { type DatabaseViewSettings } from '@databases/types/database/view';
import { createCachedSelector } from 're-reselect';

import { type DatabaseGroup } from '../database-group/database-group';
import { sortGroupsWithGroupingSettings } from '../sort-groups';

import {
  type GroupsAndSubgroupsByKey,
  selectGroupsAndSubgroupsByKey,
} from './select-groups-and-subgroups-by-key';

const EMPTY_GROUP_ARRAY: DatabaseGroup[] = [];

/**
 * Returns a sorted array of groups for entries in the view.
 * Groups are sorted based on the sort type specified in the view settings.
 */
export const selectSortedGroups = createCachedSelector<
  DefaultRootState,
  { contentId: string; viewSettings: DatabaseViewSettings; viewId: string },
  GroupsAndSubgroupsByKey,
  DatabaseViewSettings,
  DatabaseGroup[]
>(
  selectGroupsAndSubgroupsByKey,
  (state, { viewSettings }) => viewSettings,
  ({ groupsByKey }, viewSettings: DatabaseViewSettings) => {
    const fieldGrouping = viewSettings.grouping?.groupBy;

    if (!groupsByKey || !fieldGrouping) {
      return EMPTY_GROUP_ARRAY;
    }

    return sortGroupsWithGroupingSettings(groupsByKey, fieldGrouping);
  }
)((state, { viewId }) => viewId);

/**
 * Returns a sorted array of subgroups for entries in the view.
 * Subgroups are sorted based on the sort type specified in the view settings.
 */
export const selectSortedSubgroups = createCachedSelector<
  DefaultRootState,
  { contentId: string; viewSettings: DatabaseViewSettings; viewId: string },
  GroupsAndSubgroupsByKey,
  DatabaseViewSettings,
  DatabaseGroup[]
>(
  selectGroupsAndSubgroupsByKey,
  (state, { viewSettings }) => viewSettings,
  ({ subgroupsByKey }, viewSettings: DatabaseViewSettings) => {
    const fieldGrouping = viewSettings.grouping?.subgroupBy;

    if (!subgroupsByKey || !fieldGrouping) {
      return EMPTY_GROUP_ARRAY;
    }

    return sortGroupsWithGroupingSettings(subgroupsByKey, fieldGrouping);
  }
)((state, { viewId }) => viewId);
